/**
 * @fileoverview Self-contained custom element that wraps the welcome-package
 * trigger button and <dialog>.  Reacts to cart events so the button state
 * reflects whether a membership bundle is already in the cart.
 *
 * States:
 *   default  – shows the original CTA (opens the dialog)
 *   in-cart  – shows a checkout form (proceeds to checkout)
 *
 * Listened events (via pub-sub):
 *   greyson:cart:membership-bundle:added   – switch to "in-cart" state
 *   greyson:cart:membership-bundle:removed – switch back to "default" state
 */

import { subscribe, EVENTS } from '@utilities/events';

if (!customElements.get('welcome-package-modal')) {

  class WelcomePackageModal extends HTMLElement {
    #runtimeLoaded = false;
    #runtimeImportPromise = null;

    connectedCallback() {
      this.triggerSlot = this.querySelector('[data-wpm-trigger-slot]');
      this.dialog = this.querySelector('dialog');
      this.defaultTemplate = this.querySelector('[data-wpm-default-template]');
      this.checkoutTemplate = this.querySelector('[data-wpm-checkout-template]');
      this.isLoyaltyProduct = this.dataset.loyaltyTierProduct === 'true';

      if (!this.triggerSlot || !this.dialog || !this.defaultTemplate || !this.checkoutTemplate) {
        throw new Error('welcome-package-modal: missing required child elements');
      }

      this.#bindEvents();
      this.#syncInitialState();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    // ------------------------------------------------------------------
    //  Event wiring
    // ------------------------------------------------------------------

    #unsubAdded = null;
    #unsubRemoved = null;

    #bindEvents() {
      if (!this.isLoyaltyProduct) return;
      this.#unsubAdded = subscribe(EVENTS.CART_MEMBERSHIP_BUNDLE_ADDED, () => this.#setInCartState());
      this.#unsubRemoved = subscribe(EVENTS.CART_MEMBERSHIP_BUNDLE_REMOVED, () => this.#setDefaultState());
    }

    #unbindEvents() {
      this.#unsubAdded?.();
      this.#unsubRemoved?.();
    }

    // ------------------------------------------------------------------
    //  Initial state — honour server-rendered flag
    // ------------------------------------------------------------------

    #syncInitialState() {
      if (this.dataset.loyaltyTierInCart === 'true') {
        this.#setInCartState();
        return;
      }
      this.#setDefaultState();
    }

    // ------------------------------------------------------------------
    //  State transitions
    // ------------------------------------------------------------------

    #setInCartState() {
      const clone = this.checkoutTemplate.content.cloneNode(true);
      this.triggerSlot.replaceChildren(clone);
      this.dataset.state = 'in-cart';
    }

    #setDefaultState() {
      const clone = this.defaultTemplate.content.cloneNode(true);
      this.triggerSlot.replaceChildren(clone);
      this.dataset.state = 'default';
    }

    // ------------------------------------------------------------------
    //  Dialog helpers (called from inline onclick in Liquid)
    // ------------------------------------------------------------------

    openDialog(trigger) {
      this.#openDialogWithRuntime(trigger);
    }

    async #openDialogWithRuntime(trigger) {
      await this.#ensureWelcomePackageRuntime();
      this.dialog._trigger = trigger;
      this.dialog.showModal();
    }

    async #ensureWelcomePackageRuntime() {
      if (this.#runtimeLoaded) return;

      const runtimeUrl = this.dataset.welcomePackageRuntimeUrl;
      if (!runtimeUrl) {
        throw new Error('welcome-package-modal: missing runtime URL for welcome-package.js');
      }

      if (!this.#runtimeImportPromise) {
        this.#runtimeImportPromise = import(runtimeUrl);
      }

      await this.#runtimeImportPromise;
      this.#runtimeLoaded = true;
    }
  }

  customElements.define('welcome-package-modal', WelcomePackageModal);
}
